import { useEffect, useState } from 'react';
import { useReveal } from '@/hooks/useReveal';
import StepSection from './StepSection';

const rows = [
  { index: '01', label: 'Position Size', detail: 'Within the exposure cap', value: 'READY' },
  { index: '02', label: 'Risk Parameters', detail: 'Stop and target defined', value: 'CHECKED' },
  { index: '03', label: 'Execution Permission', detail: 'Automated trading enabled', value: 'READY' },
];

function StepRiskCheck() {
  const { ref, visible } = useReveal<HTMLDivElement>();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!visible) return;
    const timer = window.setTimeout(() => setReady(true), 900);
    return () => window.clearTimeout(timer);
  }, [visible]);

  return (
    <StepSection
      id="step-05"
      index="05"
      stage="filter"
      title="Risk Before Execution"
      subtitle="A trade decision is not the end of the process."
      body="Before a position is executed, the system applies its configured trading and risk parameters."
      band
    >
      <div ref={ref} className="rounded-lg border border-background-300 bg-background-50 overflow-hidden">
        <div className="flex items-center justify-between px-4 md:px-5 h-11 border-b border-background-200 bg-background-100">
          <span className="flex items-center gap-2 text-[11px] font-data tracking-widest text-foreground-700">
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                ready ? 'bg-profit-500' : 'bg-foreground-400 breathe'
              }`}
            ></span>
            RISK GATE
          </span>
          <span
            className={`text-[10px] font-data tracking-widest ${
              ready ? 'text-profit-600' : 'text-foreground-400'
            }`}
          >
            {ready ? 'READY' : 'CHECKING'}
          </span>
        </div>

        <div className="divide-y divide-background-200">
          {rows.map((row) => (
            <div key={row.index} className="flex items-center gap-3 px-4 md:px-5 py-3.5">
              <span className="w-5 shrink-0 font-data text-[11px] text-foreground-400">
                {row.index}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold text-foreground-900 truncate">{row.label}</p>
                <p className="text-[11px] text-foreground-500 truncate">{row.detail}</p>
              </div>
              <span
                className={`inline-flex shrink-0 items-center gap-1.5 font-data text-[11px] tracking-wider transition-colors duration-500 ${
                  ready ? 'text-profit-600' : 'text-foreground-400'
                }`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    ready ? 'bg-profit-500' : 'bg-foreground-400 breathe'
                  }`}
                ></span>
                {ready ? row.value : 'CHECKING'}
              </span>
            </div>
          ))}
        </div>

        <div className="space-y-2 border-t border-background-200 bg-background-100 px-4 md:px-5 py-3.5">
          <div
            className={`flex items-center justify-between rounded-md border px-3.5 py-2.5 transition-all duration-500 ${
              ready ? 'border-primary-300 bg-primary-50' : 'border-background-300 bg-background-50'
            }`}
          >
            <span className="flex items-center gap-2 font-data text-[11px] tracking-[0.2em] text-primary-700">
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  ready ? 'bg-primary-500' : 'bg-foreground-400'
                }`}
              ></span>
              PASS → MT5
            </span>
            <i className="ri-arrow-right-line text-[15px] text-primary-500"></i>
          </div>
          <div className="flex items-center justify-between rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5">
            <span className="font-data text-[11px] tracking-[0.2em] text-foreground-500">
              ELSE → REJECT
            </span>
            <span className="font-data text-[10px] text-foreground-400">NO FORCED ENTRY</span>
          </div>
        </div>
      </div>
    </StepSection>
  );
}

export default StepRiskCheck;